import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { ManagerProgressionTaskSlug } from '@sorare/core/src/__generated__/globalTypes';
import { ToastBase } from '@sorare/core/src/atoms/snackbar/ToastBase';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionToastContent } from './CollectionToastContent';
import { DailyBoostToastContent } from './DailyBoostToastContent';
import type { MissionToast_taskInterface } from './__generated__/index.graphql';

const StyledToastBase = styled(ToastBase)`
  width: 100%;
  align-self: center;

  @media ${tabletAndAbove} {
    width: calc(60 * var(--unit));
    align-self: flex-end;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  task: MissionToast_taskInterface;
  previousProgress: number;
  isLastToast: boolean;
};
export const MissionToast = ({
  open,
  onClose,
  task,
  previousProgress,
  isLastToast,
}: Props) => {
  const { parentTask } = task;
  const isDailyBoostTask =
    parentTask?.name === ManagerProgressionTaskSlug.DAILY_ACTION;
  const isCollectionTask =
    isType(task, 'CardsCountTask') ||
    isType(task, 'CompleteCollectionsCountTask');

  const hasExtraAnimation =
    isCollectionTask && task.liveScore >= task.scoreToReach;

  const onOpenChange = (o: boolean) => {
    if (!o) {
      onClose();
    }
  };
  let duration;

  if (hasExtraAnimation) {
    duration = 4000;
    if (isLastToast) {
      duration = 6000;
    }
  } else {
    duration = 2000;
    if (isLastToast) {
      duration = 4000;
    }
  }

  return (
    <StyledToastBase
      onOpenChange={onOpenChange}
      duration={duration}
      open={open}
    >
      {!!isDailyBoostTask && isType(task, 'Task') && (
        <DailyBoostToastContent task={task} onClose={onClose} />
      )}
      {!!isCollectionTask && (
        <CollectionToastContent
          task={task}
          previousProgress={previousProgress}
          onClose={onClose}
        />
      )}
    </StyledToastBase>
  );
};

MissionToast.fragments = {
  taskInterface: gql`
    fragment MissionToast_taskInterface on TaskInterface {
      id
      title
      parentTask {
        id
        name
      }
      ... on CardsCountTask {
        id
        liveScore
        scoreToReach
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        scoreToReach
      }
      ...DailyBoostToastContent_task
      ...CollectionToastContent_taskInterface
    }
    ${DailyBoostToastContent.fragments.task}
    ${CollectionToastContent.fragments.taskInterface}
  ` as TypedDocumentNode<MissionToast_taskInterface>,
};
